import { useState, useCallback, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_SOCIAL_AUTH_URL,
  HANDLE_SOCIAL_CALLBACK,
  SOCIAL_REGISTER,
} from "../graphql/queries";

export const useSocialAuth = (link) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socialData, setSocialData] = useState(null);
  const [mappedData, setMappedData] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [currentProvider, setCurrentProvider] = useState(null);

  console.log("🔍 useSocialAuth - State Update:", {
    loading,
    error,
    socialData,
    mappedData,
    missingFields,
    currentProvider,
    link,
  });

  const [getSocialAuthUrl] = useLazyQuery(GET_SOCIAL_AUTH_URL, {
    onCompleted: (data) => {
      console.log("✅ useSocialAuth - GET_SOCIAL_AUTH_URL completed:", data);
    },
    onError: (error) => {
      console.error("🚨 useSocialAuth - Error getting social auth URL:", error);
      setError("Failed to initialize social authentication");
      setLoading(false);
    },
  });

  const [handleSocialCallback] = useLazyQuery(HANDLE_SOCIAL_CALLBACK, {
    onCompleted: (data) => {
      console.log("✅ useSocialAuth - HANDLE_SOCIAL_CALLBACK completed:", data);

      if (data?.handleSocialCallback?.success) {
        const response = data.handleSocialCallback;
        console.log(
          "🔍 useSocialAuth - Processing social callback response:",
          response
        );

        // Parse JSON strings back to objects/arrays
        const parsedSocialData = response.socialData
          ? JSON.parse(response.socialData)
          : null;
        const parsedMappedData = response.mappedData
          ? JSON.parse(response.mappedData)
          : null;
        const parsedMissingFields = response.missingFields
          ? JSON.parse(response.missingFields)
          : [];

        setSocialData(parsedSocialData);
        setMappedData(parsedMappedData);
        setMissingFields(parsedMissingFields);
        setCurrentProvider(response.provider);

        console.log("🔍 useSocialAuth - State will be set to:", {
          socialData: parsedSocialData,
          mappedData: parsedMappedData,
          missingFields: parsedMissingFields,
          provider: response.provider,
        });
      } else {
        console.error("🚨 useSocialAuth - Social callback failed:", data);
        setError("Failed to authenticate with social media");
      }
      setLoading(false);
    },
    onError: (error) => {
      console.error(
        "🚨 useSocialAuth - Error handling social callback:",
        error
      );
      setError("Authentication failed. Please try again.");
      setLoading(false);
    },
  });

  const [socialRegister] = useMutation(SOCIAL_REGISTER, {
    onCompleted: (data) => {
      console.log("✅ useSocialAuth - SOCIAL_REGISTER completed:", data);
      setLoading(false);
      return data.socialRegister;
    },
    onError: (error) => {
      console.error("🚨 useSocialAuth - Error in social registration:", error);
      setError("Registration failed. Please try again.");
      setLoading(false);
    },
  });

  const startSocialAuth = useCallback(
    async (provider) => {
      console.log("🔍 useSocialAuth - Starting social auth for:", provider);

      try {
        setLoading(true);
        setError(null);
        setCurrentProvider(provider);

        console.log("🔍 useSocialAuth - Calling getSocialAuthUrl with:", {
          provider: provider.toLowerCase(),
          link,
        });

        const result = await getSocialAuthUrl({
          variables: { provider: provider.toLowerCase(), link },
        });

        console.log("🔍 useSocialAuth - getSocialAuthUrl result:", result);

        if (result.data?.getSocialAuthUrl?.authUrl) {
          const authUrl = result.data.getSocialAuthUrl.authUrl;
          const state = result.data.getSocialAuthUrl.state;

          console.log("🔍 useSocialAuth - Opening popup with:", {
            authUrl,
            state: state ? `${state.substring(0, 20)}...` : null,
          });

          const popup = window.open(
            authUrl,
            "socialAuth",
            "width=600,height=600,scrollbars=yes,resizable=yes"
          );

          const messageListener = (event) => {
            console.log("🔍 useSocialAuth - Received message:", event.data);

            // Validate origin - accept eventegration.com domains and localhost
            const allowedOrigins = [
              "https://eventegration.com",
              "https://www.eventegration.com",
              "http://localhost:3000",
              "http://localhost:3001",
            ];

            if (!allowedOrigins.includes(event.origin)) {
              console.log(
                "🚨 useSocialAuth - Invalid origin:",
                event.origin,
                "Allowed:",
                allowedOrigins
              );
              return;
            }

            console.log(
              "✅ useSocialAuth - Valid origin accepted:",
              event.origin
            );

            console.log(
              "✅ useSocialAuth - Valid origin accepted:",
              event.origin
            );

            if (event.data.type === "SOCIAL_AUTH_SUCCESS") {
              console.log(
                "✅ useSocialAuth - Social auth success, processing callback"
              );
              popup.close();
              handleCallback(
                provider,
                event.data.code,
                result.data.getSocialAuthUrl.state
              );
              window.removeEventListener("message", messageListener);
            } else if (event.data.type === "SOCIAL_AUTH_ERROR") {
              console.log(
                "🚨 useSocialAuth - Social auth error:",
                event.data.error
              );
              popup.close();
              setError(event.data.error || "Authentication failed");
              setLoading(false);
              window.removeEventListener("message", messageListener);
            }
          };

          window.addEventListener("message", messageListener);

          const checkClosed = setInterval(() => {
            if (popup.closed) {
              console.log("🔍 useSocialAuth - Popup closed by user");
              clearInterval(checkClosed);
              window.removeEventListener("message", messageListener);
              setLoading(false);
            }
          }, 1000);
        }
      } catch (err) {
        console.error("🚨 useSocialAuth - Error starting social auth:", err);
        setError("Failed to start authentication");
        setLoading(false);
      }
    },
    [getSocialAuthUrl, link]
  );

  const handleCallback = useCallback(
    async (provider, code, state) => {
      console.log("🔍 useSocialAuth - Processing callback:", {
        provider,
        code: code ? `${code.substring(0, 10)}...` : null,
        state: state ? `${state.substring(0, 20)}...` : null,
      });

      try {
        await handleSocialCallback({
          variables: {
            provider: provider.toLowerCase(),
            code,
            state,
          },
        });
      } catch (err) {
        console.error("🚨 useSocialAuth - Error handling callback:", err);
        setError("Failed to process authentication");
        setLoading(false);
      }
    },
    [handleSocialCallback]
  );

  const completeRegistration = useCallback(
    async (additionalData = {}) => {
      console.log("🔍 useSocialAuth - Completing registration with:", {
        additionalData,
        socialData,
        currentProvider,
        link,
      });

      try {
        setLoading(true);
        setError(null);

        const result = await socialRegister({
          variables: {
            link,
            provider: currentProvider.toLowerCase(),
            socialData:
              typeof socialData === "string"
                ? socialData
                : JSON.stringify(socialData),
            additionalData: JSON.stringify(additionalData),
          },
        });

        console.log("🔍 useSocialAuth - Registration result:", result);
        return result.data?.socialRegister;
      } catch (err) {
        console.error("🚨 useSocialAuth - Error completing registration:", err);
        setError("Registration failed. Please try again.");
        setLoading(false);
        throw err;
      }
    },
    [socialRegister, link, currentProvider, socialData]
  );

  const reset = useCallback(() => {
    console.log("🔍 useSocialAuth - Resetting state");
    setSocialData(null);
    setMappedData(null);
    setMissingFields([]);
    setCurrentProvider(null);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const provider = urlParams.get("provider");
    const error = urlParams.get("error");

    console.log("🔍 useSocialAuth - URL params check:", {
      code: code ? `${code.substring(0, 10)}...` : null,
      state: state ? `${state.substring(0, 20)}...` : null,
      provider,
      error,
    });

    if (error) {
      setError("Authentication failed: " + error);
    } else if (code && state && provider) {
      handleCallback(provider, code, state);

      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [handleCallback]);

  return {
    loading,
    error,
    socialData,
    mappedData,
    missingFields,
    currentProvider,
    startSocialAuth,
    completeRegistration,
    reset,
    isAuthenticated: !!socialData,
    hasRequiredData: missingFields.length === 0,
  };
};

export default useSocialAuth;
